'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { cn } from '@utils/index';
import { CalendarRange } from 'lucide-react';
import momentTz from 'moment-timezone';
import React, { type FC, useCallback, useEffect, useId, useRef, useState } from 'react';

import type { SupportedTimeZone } from '@/constants/date';
import { type DatePresetName, getPresetDateRange } from '@/utils/date';

import { Button } from './button';
import { Calendar } from './calendar';
import { DateInput } from './date-input';
import { Input } from './input';
import { Label } from './label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Switch } from './switch';

export interface DateRangePickerProps {
  /** Click handler for applying the updates from DateRangePicker. */
  onUpdate?: (values: { range: DateRange; rangeCompare?: DateRange }) => void;
  /** Initial value for start date */
  initialDateFrom?: Date | string;
  /** Initial value for end date */
  initialDateTo?: Date | string;
  /** Initial value for start date for compare */
  initialCompareFrom?: Date | string;
  /** Initial value for end date for compare */
  initialCompareTo?: Date | string;
  /** Alignment of popover */
  align?: 'start' | 'center' | 'end';
  /** Option for locale */
  locale?: string;
  /** Option for showing compare feature */
  showCompare?: boolean;
  /**
   * Timezone for calculating preset date ranges (Today, Yesterday, etc.)
   * When provided, presets will be calculated in this timezone instead of browser local time.
   * This ensures "Yesterday" in the picker matches "Yesterday" in the table display.
   */
  timezone?: SupportedTimeZone;
  /**
   * Optional placeholder shown on the trigger when no initial date range is provided
   * (both initialDateFrom and initialDateTo are undefined). Lets callers represent
   * an "all dates / no filter" state instead of forcing the picker to default to today.
   */
  placeholder?: string;
  /**
   * Also pick a start and an end time (HH:mm, 24-hour). The range passed to
   * `onUpdate` then carries those times instead of whole days, and a string
   * initial value may be a naive `YYYY-MM-DDTHH:mm`. Off by default: every
   * other caller keeps whole-day ranges.
   */
  showTime?: boolean;
}

const DAY_START_TIME = '00:00';
const DAY_END_TIME = '23:59';
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** The HH:mm part of an initial value, or `fallback` when it has none. */
const initialTimeOf = (value: Date | string | undefined, fallback: string): string => {
  if (value === undefined) return fallback;
  if (typeof value === 'string') {
    const time = value.split('T')[1]?.slice(0, 5);
    return time && TIME_PATTERN.test(time) ? time : fallback;
  }
  return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
};

/** A copy of `date` (calendar day) at `time` (HH:mm); seconds are 0. */
const withTime = (date: Date, time: string): Date => {
  const match = TIME_PATTERN.exec(time);
  const result = new Date(date);
  result.setHours(match ? Number(match[1]) : 0, match ? Number(match[2]) : 0, 0, 0);
  return result;
};

const formatDateTime = (date: Date, locale: string = 'en-us'): string => {
  return date.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (date: Date, locale: string = 'en-us'): string => {
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getDateAdjustedForTimezone = (dateInput: Date | string): Date => {
  if (typeof dateInput === 'string') {
    // Split the date string to get year, month, and day parts
    const parts = dateInput.split('-').map(part => parseInt(part, 10));
    // Create a new Date object using the local timezone
    // Note: Month is 0-indexed, so subtract 1 from the month part
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return date;
  } else {
    // If dateInput is already a Date object, return it directly
    return dateInput;
  }
};

/**
 * Create a Date object set to start of day (00:00:00.000).
 * Note: year/month/day should be pre-calculated in the target timezone.
 * This creates a Date object representing that calendar date in browser local time.
 */
const createStartOfDay = (year: number, month: number, day: number): Date => {
  return new Date(year, month, day, 0, 0, 0, 0);
};

/**
 * Create a Date object set to end of day (23:59:59.999).
 * Note: year/month/day should be pre-calculated in the target timezone.
 * This creates a Date object representing that calendar date in browser local time.
 */
const createEndOfDay = (year: number, month: number, day: number): Date => {
  return new Date(year, month, day, 23, 59, 59, 999);
};

interface DateRange {
  from: Date;
  to: Date | undefined;
}

interface Preset {
  name: DatePresetName;
  label: string;
}

// Define presets with typed names for compile-time safety
const PRESETS: Preset[] = [
  { name: 'today', label: 'Today' },
  { name: 'yesterday', label: 'Yesterday' },
  { name: 'last7', label: 'Last 7 days' },
  { name: 'last14', label: 'Last 14 days' },
  { name: 'last30', label: 'Last 30 days' },
  { name: 'last60', label: 'Last 60 days' },
  { name: 'last90', label: 'Last 90 days' },
];

/** The DateRangePicker component allows a user to select a range of dates */
export const DateRangePicker: FC<DateRangePickerProps> & {
  filePath: string;
} = ({
  initialDateFrom,
  initialDateTo,
  initialCompareFrom,
  initialCompareTo,
  onUpdate,
  align = 'end',
  locale = 'en-US',
  showCompare = true,
  timezone,
  placeholder,
  showTime = false,
}): JSX.Element => {
  // If neither initialDateFrom nor initialDateTo is supplied we treat the picker as
  // "no range" — the trigger shows the placeholder, and we seed the popover with
  // today so the calendar still opens to a sensible starting point.
  // Important: keep `getInitialRange`'s useCallback dependencies tied directly to the
  // prop refs. Computing a `new Date()` fallback at the component-function scope
  // would change ref every render, churn the callback identity, and trigger the
  // reset useEffect below — which would overwrite the user's in-progress selection.
  const hasInitialRange = initialDateFrom !== undefined || initialDateTo !== undefined;

  const getInitialRange = useCallback((): DateRange => {
    const fallback = new Date(new Date().setHours(0, 0, 0, 0));
    const from = initialDateFrom ?? fallback;
    return {
      from: getDateAdjustedForTimezone(from),
      to: initialDateTo
        ? getDateAdjustedForTimezone(initialDateTo)
        : getDateAdjustedForTimezone(from),
    };
  }, [initialDateFrom, initialDateTo]);

  const getInitialCompareRange = useCallback(
    (): DateRange | undefined =>
      initialCompareFrom
        ? {
            from:
              typeof initialCompareFrom === 'string'
                ? getDateAdjustedForTimezone(initialCompareFrom)
                : initialCompareFrom,
            to: initialCompareTo
              ? typeof initialCompareTo === 'string'
                ? getDateAdjustedForTimezone(initialCompareTo)
                : initialCompareTo
              : typeof initialCompareFrom === 'string'
                ? getDateAdjustedForTimezone(initialCompareFrom)
                : initialCompareFrom,
          }
        : undefined,
    [initialCompareFrom, initialCompareTo],
  );

  const getInitialTimes = useCallback(
    (): { from: string; to: string } => ({
      from: initialTimeOf(initialDateFrom, DAY_START_TIME),
      to: initialTimeOf(initialDateTo, DAY_END_TIME),
    }),
    [initialDateFrom, initialDateTo],
  );

  const [isOpen, setIsOpen] = useState(false);

  const [range, setRange] = useState<DateRange>(getInitialRange);
  // Only read when showTime is set; whole-day pickers ignore them.
  const [times, setTimes] = useState(getInitialTimes);
  const openedTimesRef = useRef(times);
  const timeFieldId = useId();
  const setTime = (bound: 'from' | 'to', value: string): void => {
    // A preset names whole days; custom times no longer match it.
    setSelectedPreset(undefined);
    setTimes(prev => ({ ...prev, [bound]: value }));
  };
  const [rangeCompare, setRangeCompare] = useState<DateRange | undefined>(getInitialCompareRange);

  // Refs to store the values of range and rangeCompare when the date picker is opened
  const openedRangeRef = useRef<DateRange | undefined>();
  const openedRangeCompareRef = useRef<DateRange | undefined>();

  const [selectedPreset, setSelectedPreset] = useState<string | undefined>(undefined);

  // Date currently under the cursor while the user is choosing the end of the range.
  // Drives the live "preview" shading between the anchor and the hovered day.
  const [hoveredDate, setHoveredDate] = useState<Date | undefined>(undefined);

  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false,
  );

  useEffect(() => {
    const handleResize = (): void => {
      setIsSmallScreen(window.innerWidth < 960);
    };

    window.addEventListener('resize', handleResize);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getPresetRange = useCallback(
    (presetName: DatePresetName): DateRange => {
      const preset = PRESETS.find(({ name }) => name === presetName);
      if (!preset) throw new Error(`Unknown date range preset: ${presetName}`);

      // If timezone is provided, use the shared utility for timezone-aware calculations
      // Otherwise fall back to browser local time (original behavior)
      if (timezone) {
        const dateRange = getPresetDateRange(preset.name, timezone);
        return {
          from: createStartOfDay(dateRange.from.year, dateRange.from.month, dateRange.from.day),
          to: createEndOfDay(dateRange.to.year, dateRange.to.month, dateRange.to.day),
        };
      }

      // Fallback: use browser local time (original behavior for backwards compatibility)
      const from = new Date();
      const to = new Date();

      switch (preset.name) {
        case 'today':
          from.setHours(0, 0, 0, 0);
          to.setHours(23, 59, 59, 999);
          break;
        case 'yesterday':
          from.setDate(from.getDate() - 1);
          from.setHours(0, 0, 0, 0);
          to.setDate(to.getDate() - 1);
          to.setHours(23, 59, 59, 999);
          break;
        case 'last7':
          from.setDate(from.getDate() - 6);
          from.setHours(0, 0, 0, 0);
          to.setHours(23, 59, 59, 999);
          break;
        case 'last14':
          from.setDate(from.getDate() - 13);
          from.setHours(0, 0, 0, 0);
          to.setHours(23, 59, 59, 999);
          break;
        case 'last30':
          from.setDate(from.getDate() - 29);
          from.setHours(0, 0, 0, 0);
          to.setHours(23, 59, 59, 999);
          break;
        case 'last60':
          from.setDate(from.getDate() - 59);
          from.setHours(0, 0, 0, 0);
          to.setHours(23, 59, 59, 999);
          break;
        case 'last90':
          from.setDate(from.getDate() - 89);
          from.setHours(0, 0, 0, 0);
          to.setHours(23, 59, 59, 999);
          break;
      }

      return { from, to };
    },
    [timezone],
  );

  /**
   * Calculate a compare range (same dates from previous year) for a given date range.
   * Uses moment.subtract to handle leap years correctly (Feb 29 -> Feb 28).
   */
  const calculateCompareRange = (fromDate: Date, toDate: Date | undefined): DateRange => {
    const fromMoment = momentTz(fromDate).subtract(1, 'year');
    // Create toMoment independently to avoid issues with moment mutation
    const toMoment = toDate
      ? momentTz(toDate).subtract(1, 'year')
      : momentTz(fromDate).subtract(1, 'year');

    return {
      from: createStartOfDay(fromMoment.year(), fromMoment.month(), fromMoment.date()),
      to: createEndOfDay(toMoment.year(), toMoment.month(), toMoment.date()),
    };
  };

  const setPreset = (preset: DatePresetName): void => {
    const range = getPresetRange(preset);
    setRange(range);
    setTimes({ from: DAY_START_TIME, to: DAY_END_TIME });
    if (rangeCompare) {
      setRangeCompare(calculateCompareRange(range.from, range.to));
    }
  };

  /**
   * Compare two dates by their calendar date components (year, month, day) only.
   * This avoids timezone issues that can occur when comparing Date timestamps.
   */
  const isSameCalendarDate = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const checkPreset = useCallback((): void => {
    for (const preset of PRESETS) {
      const presetRange = getPresetRange(preset.name);

      // Compare calendar dates directly using year/month/day components
      // This avoids timezone issues that can occur with timestamp-based comparisons
      const fromMatches = isSameCalendarDate(range.from, presetRange.from);
      const toMatches =
        range.to && presetRange.to
          ? isSameCalendarDate(range.to, presetRange.to)
          : !range.to && !presetRange.to;

      if (fromMatches && toMatches) {
        setSelectedPreset(preset.name);
        return;
      }
    }

    setSelectedPreset(undefined);
  }, [getPresetRange, range]);

  const resetValues = (): void => {
    setRange(getInitialRange());
    setTimes(getInitialTimes());
    setRangeCompare(getInitialCompareRange());
    setHoveredDate(undefined);
  };

  useEffect(() => {
    checkPreset();
  }, [checkPreset]);

  useEffect(() => {
    // Sync external prop changes into local state ONLY while the popover is closed.
    // The parent (/cases) refetches every ~30s and re-mints the initial date props on each
    // render (e.g. `naiveStringToDate(dateFrom)` returns a fresh Date), which churns this
    // effect. Running it while the popover is open would overwrite the user's in-progress
    // selection with the values from when the picker was opened. Closing the popover
    // re-syncs via resetValues() in onOpenChange.
    if (isOpen) return;
    setRange(getInitialRange());
    setTimes(getInitialTimes());
    setRangeCompare(getInitialCompareRange());
  }, [getInitialCompareRange, getInitialRange, getInitialTimes, isOpen]);

  const PresetButton = ({
    preset,
    label,
    isSelected,
  }: {
    preset: DatePresetName;
    label: string;
    isSelected: boolean;
  }): JSX.Element => (
    <Button
      className={cn('w-full justify-start font-normal', isSelected && 'pointer-events-none')}
      variant="ghost"
      size="sm"
      onClick={() => {
        setPreset(preset);
      }}
    >
      <>
        <span className={cn('mr-2 opacity-0', isSelected && 'opacity-100')}>
          <CheckIcon width={16} height={16} />
        </span>
        {label}
      </>
    </Button>
  );

  // Helper function to check if two date ranges are equal
  const areRangesEqual = (a?: DateRange, b?: DateRange): boolean => {
    if (!a || !b) return a === b; // If either is undefined, return true if both are undefined
    return (
      a.from.getTime() === b.from.getTime() && (!a.to || !b.to || a.to.getTime() === b.to.getTime())
    );
  };

  // True while an anchor (from) is set but the end (to) has not been chosen yet.
  const isSelectingEnd = Boolean(range.from && !range.to);

  // With times, the range handed to onUpdate is the chosen days at the chosen
  // times; a start at or after the end (same day) cannot be applied.
  const timesValid = TIME_PATTERN.test(times.from) && TIME_PATTERN.test(times.to);
  const timedRange: DateRange = {
    from: withTime(range.from, times.from),
    to: withTime(range.to ?? range.from, times.to),
  };
  const timeRangeError =
    showTime && !isSelectingEnd
      ? !timesValid
        ? 'Enter a start and an end time.'
        : timedRange.to && timedRange.from >= timedRange.to
          ? 'The start must be before the end.'
          : null
      : null;
  const formatBound = (date: Date, time: string): string =>
    showTime ? formatDateTime(withTime(date, time), locale) : formatDate(date, locale);

  /**
   * Selection model: anchor -> hover-preview -> auto-restart.
   * - A click with no in-progress selection (or on an already-complete range) starts a fresh
   *   selection from the clicked day. This replaces the undiscoverable "double-click to reset".
   * - The next click completes the range, swapping the endpoints when the second pick precedes
   *   the anchor so the user never has to click in chronological order.
   * - Clicking the same day twice yields a valid single-day range (from === to).
   */
  const handleDayClick = (day: Date): void => {
    setSelectedPreset(undefined);
    const hasCompleteRange = Boolean(range.from && range.to);
    if (hasCompleteRange) {
      setRange({ from: day, to: undefined });
      return;
    }
    if (day < range.from) {
      setRange({ from: day, to: range.from });
    } else {
      setRange({ from: range.from, to: day });
    }
    setHoveredDate(undefined);
  };

  // Tentative range shown while hovering, normalized so `from <= to` regardless of direction.
  const previewRange =
    isSelectingEnd && hoveredDate
      ? hoveredDate < range.from
        ? { from: hoveredDate, to: range.from }
        : { from: range.from, to: hoveredDate }
      : undefined;

  return (
    <Popover
      modal={true}
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (open) {
          openedRangeRef.current = range;
          openedRangeCompareRef.current = rangeCompare;
          openedTimesRef.current = times;
        }
        if (!open) {
          resetValues();
        }
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          size="default"
          variant="outline"
          className={cn(
            'justify-between text-left font-normal px-3',
            showTime ? 'w-[300px]' : 'w-[260px]',
          )}
        >
          <div className="flex items-center gap-2 min-w-0">
            <CalendarRange className="h-4 w-4 opacity-70 shrink-0" />
            <span
              className={`text-sm truncate ${!hasInitialRange && placeholder ? 'text-muted-foreground' : ''}`}
            >
              {!hasInitialRange && placeholder
                ? placeholder
                : `${formatBound(range.from, times.from)} - ${formatBound(
                    range.to ?? range.from,
                    times.to,
                  )}`}
            </span>
          </div>
          <ChevronDownIcon className="ml-2 h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} side="bottom" sideOffset={4} className="w-[520px] p-0 scale-95">
        <div className="flex">
          <div className="flex">
            <div className="flex flex-col">
              {showCompare && (
                <div className="flex items-center space-x-2 pr-4">
                  <Switch
                    defaultChecked={Boolean(rangeCompare)}
                    onCheckedChange={(checked: boolean) => {
                      if (checked) {
                        if (!range.to) {
                          setRange({
                            from: range.from,
                            to: range.from,
                          });
                        }
                        setRangeCompare(calculateCompareRange(range.from, range.to ?? range.from));
                      } else {
                        setRangeCompare(undefined);
                      }
                    }}
                    id="compare-mode"
                  />
                  <Label htmlFor="compare-mode">Compare</Label>
                </div>
              )}
              <div className="flex flex-col gap-2">
                {rangeCompare != null && (
                  <div className="flex gap-2">
                    <DateInput
                      value={rangeCompare?.from}
                      onChange={(date: Date) => {
                        if (rangeCompare) {
                          const compareToDate =
                            rangeCompare.to == null || date > rangeCompare.to
                              ? date
                              : rangeCompare.to;
                          setRangeCompare(prevRangeCompare => ({
                            ...prevRangeCompare,
                            from: date,
                            to: compareToDate,
                          }));
                        } else {
                          setRangeCompare({
                            from: date,
                            to: new Date(),
                          });
                        }
                      }}
                    />
                    <div className="py-1">-</div>
                    <DateInput
                      value={rangeCompare?.to}
                      onChange={(date: Date) => {
                        if (rangeCompare && rangeCompare.from) {
                          const compareFromDate =
                            date < rangeCompare.from ? date : rangeCompare.from;
                          setRangeCompare({
                            ...rangeCompare,
                            from: compareFromDate,
                            to: date,
                          });
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              {isSmallScreen && (
                <Select
                  defaultValue={selectedPreset}
                  onValueChange={value => {
                    setPreset(value as DatePresetName);
                  }}
                >
                  <SelectTrigger className="w-[180px] mx-auto mb-2">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PRESETS.map(preset => (
                      <SelectItem key={preset.name} value={preset.name}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="relative">
                <Calendar
                  style={
                    {
                      // Sizing only — all selection/hover colors are driven by Tailwind theme
                      // tokens in calendar.tsx so the start/middle/end/preview tiers stay
                      // consistent (and readable) across light/dark themes. The hover overrides
                      // are neutralized so the ghost-button hover styling owns hover appearance.
                      '--rdp-cell-size': '32px',
                      '--rdp-caption-font-size': '0.875rem',
                      '--rdp-font-size': '0.813rem',
                      '--rdp-day-hover-background-color': 'transparent',
                      '--rdp-day-hover-color': 'inherit',
                      '--rdp-day_button-border-radius': '0.375rem',
                    } as React.CSSProperties
                  }
                  className="scale-90 [&_.rdp-day:not([data-selected=true]):hover]:bg-accent [&_.rdp-day:not([data-selected=true]):hover]:text-accent-foreground [&_.rdp-day:hover]:rounded-md [&_.rdp-day:hover]:transition-colors min-w-80"
                  mode="range"
                  selected={range}
                  // Provide onSelect so react-day-picker treats the range as *controlled*
                  // (without it, rdp falls back to its own internal selection state and ignores
                  // our `selected` prop — see useRange.js). We ignore rdp's computed range and
                  // drive selection from the clicked day via our anchor/preview/restart model.
                  onSelect={(_range, selectedDay: Date) => {
                    handleDayClick(selectedDay);
                  }}
                  onDayMouseEnter={(day: Date) => {
                    // Only track hover while choosing the end date — avoids needless
                    // re-renders when the range is already complete.
                    if (isSelectingEnd) setHoveredDate(day);
                  }}
                  onDayMouseLeave={() => {
                    setHoveredDate(undefined);
                  }}
                  modifiers={{
                    preview: previewRange ?? false,
                    preview_end: isSelectingEnd && hoveredDate ? hoveredDate : false,
                  }}
                  numberOfMonths={1}
                  defaultMonth={new Date()}
                />
              </div>
              {showTime && (
                <div className="flex items-end gap-2 px-3 pb-3">
                  {(['from', 'to'] as const).map(bound => (
                    <div key={bound} className="flex-1">
                      <Label
                        htmlFor={`${timeFieldId}-${bound}`}
                        className="text-xs text-muted-foreground"
                      >
                        {bound === 'from' ? 'Start time' : 'End time'}
                        {timezone ? ` (${timezone})` : ''}
                      </Label>
                      <Input
                        id={`${timeFieldId}-${bound}`}
                        type="time"
                        step={60}
                        value={times[bound]}
                        onChange={e => setTime(bound, e.target.value)}
                        className="mt-1 h-9"
                        aria-invalid={timeRangeError ? true : undefined}
                        aria-describedby={timeRangeError ? `${timeFieldId}-summary` : undefined}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {!isSmallScreen && (
            <div className="flex flex-col items-end gap-1 border-l">
              <div className="flex w-full flex-col items-end gap-1 px-3 py-3">
                {PRESETS.map(preset => (
                  <PresetButton
                    key={preset.name}
                    preset={preset.name}
                    label={preset.label}
                    isSelected={selectedPreset === preset.name}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 p-3 border-t">
          <span
            id={`${timeFieldId}-summary`}
            className={cn(
              'text-xs truncate',
              timeRangeError ? 'text-destructive' : 'text-muted-foreground',
            )}
            aria-live="polite"
          >
            {isSelectingEnd
              ? `${formatBound(range.from, times.from)} → select end date`
              : (timeRangeError ??
                `${formatBound(range.from, times.from)} → ${formatBound(
                  range.to ?? range.from,
                  times.to,
                )}`)}
          </span>
          <div className="flex gap-2 shrink-0">
            <Button
              onClick={() => {
                setIsOpen(false);
                resetValues();
              }}
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              disabled={isSelectingEnd || !!timeRangeError}
              onClick={() => {
                setIsOpen(false);
                const timesChanged =
                  showTime &&
                  (times.from !== openedTimesRef.current.from ||
                    times.to !== openedTimesRef.current.to);
                if (
                  timesChanged ||
                  !areRangesEqual(range, openedRangeRef.current) ||
                  !areRangesEqual(rangeCompare, openedRangeCompareRef.current)
                ) {
                  onUpdate?.({ range: showTime ? timedRange : range, rangeCompare });
                }
              }}
            >
              Update
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

DateRangePicker.displayName = 'DateRangePicker';
DateRangePicker.filePath = 'libs/shared/ui-kit/src/lib/date-range-picker/date-range-picker.tsx';
